"use client";

import { useAnalytics } from "@analytics";
import { MARKETING_EVENTS, PRODUCT_EVENTS } from "@repo/analytics";
import type { CTAClickedData, PurchaseInitiatedData } from "@repo/analytics";
import { type Config, config } from "@repo/config";
import { useActiveOrganization } from "@saas/organizations/hooks/use-active-organization";
import { useOrganizationPlan } from "@saas/organizations/lib/api";
import {
	PaymentsApiError,
	useCreateCheckoutLinkMutation,
	useCreateCustomerPortalLinkMutation,
} from "@saas/payments/lib/api";
import {
	currentPlanPrice,
	isCurrentPlanPrice,
	planChangeTargetPriceId,
} from "@saas/payments/lib/pricing-v2";
import type { PlanId } from "@saas/payments/types";
import { useLocaleCurrency } from "@shared/hooks/locale-currency";
import { useRouter } from "@shared/hooks/router";
import { apiClient } from "@shared/lib/api-client";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";

const plans = config.payments.plans as Config["payments"]["plans"];

export type CheckoutInterval = "month" | "quarter" | "year";

/**
 * Plan selection for the pricing table (v1 and v2): signup redirect for
 * visitors, the onboarding callback in inline mode, the PAYG credits modal,
 * the billing portal's confirmation page for the chosen price for an org that
 * already pays for a subscription, and Stripe Checkout for everyone else.
 * Selecting the price an org already pays (same plan tier and billing
 * interval) does nothing.
 */
export function usePlanCheckout({
	userId,
	propertyId,
	organizationSlug,
	organizationId,
	inlineMode = false,
	onPlanSelected,
}: {
	userId?: string;
	propertyId?: string;
	organizationSlug?: string;
	organizationId?: string;
	inlineMode?: boolean;
	onPlanSelected?: (planId: string, interval: string) => void;
}) {
	const t = useTranslations();
	const router = useRouter();
	const localeCurrency = useLocaleCurrency();
	const [loading, setLoading] = useState<PlanId | false>(false);
	const [paygModalOpen, setPaygModalOpen] = useState(false);
	const [paygCheckoutLoading, setPaygCheckoutLoading] = useState(false);
	const { trackEvent } = useAnalytics();

	// Get organizationId from active organization hook if not provided as prop
	const { activeOrganization } = useActiveOrganization();
	const effectiveOrganizationId = organizationId || activeOrganization?.id;
	const effectiveOrganizationSlug =
		organizationSlug || activeOrganization?.slug;

	// Check if user is on a trial - trial users should be able to select their current plan to upgrade
	const { data: planInfo } = useOrganizationPlan(effectiveOrganizationId, {
		enabled: !!effectiveOrganizationId,
	});
	const isTrialUser =
		planInfo?.hasTrialSubscription && !planInfo?.hasActiveSubscription;
	const isExpiredTrial = planInfo?.hasExpiredTrial;

	const createCheckoutLinkMutation = useCreateCheckoutLinkMutation();
	const createCustomerPortalLinkMutation =
		useCreateCustomerPortalLinkMutation();

	// Handle PAYG checkout after user selects credits in modal
	const handlePaygCheckout = async (credits: number) => {
		if (!effectiveOrganizationId || !effectiveOrganizationSlug) {
			return;
		}

		setPaygCheckoutLoading(true);
		try {
			const redirectUrl = propertyId
				? `${window.location.origin}/app/${effectiveOrganizationSlug}/properties/${propertyId}`
				: `${window.location.origin}/app/${effectiveOrganizationSlug}/settings/billing`;

			const response = await apiClient.payments[
				"create-credits-checkout"
			].$post({
				json: {
					organizationId: effectiveOrganizationId,
					credits,
					redirectUrl,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to create PAYG checkout");
			}

			const data = await response.json();
			if (data.checkoutUrl) {
				window.location.href = data.checkoutUrl;
			}
		} catch (error) {
			console.error("PAYG checkout error:", error);
			toast.error(t("pricing.checkoutError"));
			setPaygCheckoutLoading(false);
			setPaygModalOpen(false);
		}
	};

	const onSelectPlan = async (
		planId: PlanId,
		interval: CheckoutInterval,
		productId?: string,
	) => {
		// A subscriber can never start a switch to the price they already
		// pay; the pricing table disables that button, this guards any other
		// caller.
		if (isCurrentPlanPrice(currentPlanPrice(planInfo), planId, interval)) {
			return;
		}

		const plan = plans[planId];
		const price = plan.prices?.find(
			(price) => price.productId === productId,
		);

		// Track CTA click
		trackEvent(MARKETING_EVENTS.CTA_CLICKED, {
			plan: planId,
			price: price?.amount || 0,
			currency: price?.currency || localeCurrency,
			location: inlineMode ? "onboarding_plan_selector" : "pricing_table",
			isLoggedIn: !!userId,
		} as CTAClickedData);

		// If in inline mode (onboarding), call callback instead of redirecting
		if (inlineMode && onPlanSelected) {
			onPlanSelected(planId, interval);
			return;
		}

		if (!userId) {
			// Pass plan selection to signup page
			router.push(`/auth/signup?plan=${planId}&interval=${interval}`);
			return;
		}

		if (!price) {
			return;
		}

		setLoading(planId);

		try {
			// Track purchase initiation
			trackEvent(PRODUCT_EVENTS.PURCHASE_INITIATED, {
				plan: planId,
				price: price.amount,
				currency: price.currency,
				propertyId,
				organizationId: organizationSlug,
			} as PurchaseInitiatedData);

			// Build redirect URL - go to property page if propertyId exists, otherwise billing page
			const redirectUrl = propertyId
				? `${window.location.origin}/app/${effectiveOrganizationSlug}/properties/${propertyId}`
				: `${window.location.origin}/app/${effectiveOrganizationSlug}/settings/billing`;

			// Special handling for PAYG credits - show modal to let user choose credit amount
			if (planId === "paygCredits" && effectiveOrganizationId) {
				setLoading(false);
				setPaygModalOpen(true);
				return;
			}

			// An organization that already pays for a subscription changes plans
			// in the Stripe billing portal: a new Checkout would open a SECOND
			// subscription and bill both (the API refuses it with a 409). The
			// customer already picked the plan and interval here, so the portal
			// opens straight on the confirmation page for that exact price, and
			// Stripe sends them back to billing with `planChanged=1`.
			if (
				price.type === "recurring" &&
				planInfo?.hasActiveSubscription &&
				effectiveOrganizationId
			) {
				const targetPriceId = planChangeTargetPriceId(planId, interval);
				if (!targetPriceId) {
					throw new Error(
						`No ${interval} price configured for plan ${planId}`,
					);
				}
				const { customerPortalLink } =
					await createCustomerPortalLinkMutation.mutateAsync({
						organizationId: effectiveOrganizationId,
						redirectUrl: `${window.location.origin}/app/${effectiveOrganizationSlug}/settings/billing`,
						targetPriceId,
					});
				window.location.href = customerPortalLink;
				return;
			}

			const { checkoutLink } =
				await createCheckoutLinkMutation.mutateAsync({
					type:
						price.type === "one-time" ? "one-time" : "subscription",
					productId: price.productId,
					propertyId,
					organizationId: effectiveOrganizationId,
					redirectUrl,
				});

			window.location.href = checkoutLink;
		} catch (error) {
			console.error(error);
			if (
				error instanceof PaymentsApiError &&
				error.code === "PLAN_CHANGE_UNAVAILABLE"
			) {
				toast.error(t("pricing.v2.planChangeUnavailable.message"), {
					duration: 15000,
					action: {
						label: t("pricing.v2.planChangeUnavailable.contact"),
						onClick: () => router.push("/contact"),
					},
				});
			} else if (
				error instanceof PaymentsApiError &&
				error.code === "ALREADY_ON_PLAN"
			) {
				toast.error(t("pricing.v2.alreadyOnPlan"));
			} else {
				toast.error(t("pricing.checkoutError"));
			}
		} finally {
			setLoading(false);
		}
	};

	return {
		onSelectPlan,
		loading,
		planInfo,
		isTrialUser,
		isExpiredTrial,
		paygModal: {
			open: paygModalOpen,
			onOpenChange: (open: boolean) => {
				setPaygModalOpen(open);
				if (!open) {
					setPaygCheckoutLoading(false);
				}
			},
			onConfirm: handlePaygCheckout,
			isLoading: paygCheckoutLoading,
		},
	};
}
